import debounce from 'lodash/debounce'
import { useEffect } from 'react'

import { GUEST_INBOX_V2_EMAILS } from '@new/__global/constants/emailsList'
import { useEmailPermission } from '@new/__global/hooks/useEmailPermission'

import { queryClient } from '@queries/query-client'

import { stayTimelineQueryKeys } from '../queries/queryKeys'
import { subscribeToStayTimeline } from '../stores/api/stayTimelineLiveArriere'

interface UseStayTimelineLiveParams {
  bookingId?: string
  isVisible: boolean
}

export const useStayTimelineLive = ({ bookingId, isVisible }: UseStayTimelineLiveParams) => {
  const isBetaTester = useEmailPermission({ allowedEmails: GUEST_INBOX_V2_EMAILS })
  const url: string | undefined = import.meta.env.VITE_ARRIERE_BACKOFFICE_HIVE_URL
  const isEnabled = isBetaTester && isVisible

  useEffect(() => {
    if (!bookingId || !url || !isEnabled) return

    let controller: AbortController | undefined
    let wasLive = false

    const invalidateTimeline = () => {
      void queryClient.invalidateQueries({
        queryKey: stayTimelineQueryKeys.bookingConversations(bookingId),
      })
    }

    const refetchTimeline = debounce(invalidateTimeline, 300, { maxWait: 1500 })

    const unsubscribe = () => {
      controller?.abort()
      refetchTimeline.cancel()

      if (wasLive) invalidateTimeline()

      wasLive = false
    }

    const handleVisibilityChange = () => {
      unsubscribe()

      if (document.visibilityState === 'hidden') return

      controller = new AbortController()
      subscribeToStayTimeline({
        bookingId,
        url,
        signal: controller.signal,
        onChange: refetchTimeline,
        onLive: isLive => {
          if (isLive !== wasLive) refetchTimeline()
          wasLive = isLive
        },
      })
    }

    handleVisibilityChange()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      unsubscribe()
    }
  }, [bookingId, isEnabled, url])
}
